/**
 * FaintOverlayScene -- gentle multi-phase cinematic triggered when the local
 * player's HP reaches 0 (B-017 faint flow, F10 sprint-1-room-1).
 *
 * The sequence:
 *   Phase 1 (0-2s):   Desaturation + gentle camera shake
 *   Phase 2 (2-5s):   Poetic text: "The world grew heavy..."
 *   Phase 3 (5-7s):   Fade to deep blue (#1a1a2e)
 *   Phase 4 (7-9s):   Recovery text: "...but the hearth remembered you."
 *                      Fade back in, remove effects, re-enable input.
 *
 * Runs ON TOP of the game scene. Blocks all input during the sequence.
 * Uses Phaser 4 Filters API (ColorMatrix) for desaturation, matching the
 * pattern established by TimeOfDayColorGrader.
 *
 * The weakness debuff timestamp is stored in localStorage so it survives
 * page reloads.
 */

import * as Phaser from 'phaser';
import { FAINT_OVERLAY_SCENE_KEY } from './sceneKeys';
import type { PlayerFaintEvent } from '@game/shared';

// ---- Style constants matching art-bible / SleepOverlayScene ----

const TITLE_FONT = "'Almendra', serif";
const COLOR_INK = '#eae0c8';
const DEEP_BLUE = 0x1a1a2e;

// ---- Phase timings (ms) ----

const PHASE_1_DURATION = 2000;
const PHASE_2_FADE_IN = 800;
const PHASE_2_HOLD = 2000;
const PHASE_2_FADE_OUT = 800;
const PHASE_3_FADE_DURATION = 2000;
const PHASE_4_FADE_IN = 1000;
const PHASE_4_TEXT_FADE_IN = 600;
const PHASE_4_TEXT_HOLD = 2000;
const PHASE_4_TEXT_FADE_OUT = 800;

// ---- localStorage key for weakness debuff ----

export const WEAKNESS_STORAGE_KEY = 'faint:weakenedUntil';

export class FaintOverlayScene extends Phaser.Scene {
  public static readonly KEY = FAINT_OVERLAY_SCENE_KEY;

  /** The faint event payload passed via scene init data. */
  private faintEvent: PlayerFaintEvent | null = null;

  /** ColorMatrix filter applied to the underlying game camera. */
  private cmFilter: Phaser.Filters.ColorMatrix | null = null;
  /** Reference to the game camera we attached the filter to. */
  private targetCamera: Phaser.Cameras.Scene2D.Camera | null = null;

  /** Full-screen rectangle used for the fade-to-black effect. */
  private fadeRect: Phaser.GameObjects.Rectangle | null = null;

  constructor() {
    super({ key: FAINT_OVERLAY_SCENE_KEY });
  }

  init(data?: { faintEvent?: PlayerFaintEvent }): void {
    this.faintEvent = data?.faintEvent ?? null;
  }

  create(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    // Block all input to underlying scenes with an invisible interactive rect.
    this.add
      .rectangle(cx, cy, this.scale.width, this.scale.height, 0x000000, 0)
      .setInteractive()
      .setScrollFactor(0)
      .setDepth(0);

    // Persist weakness timestamp
    if (this.faintEvent) {
      try {
        localStorage.setItem(
          WEAKNESS_STORAGE_KEY,
          String(this.faintEvent.weakenedUntil),
        );
      } catch {
        // localStorage may be unavailable; non-critical.
      }
      // Also store on the game registry for the HUD to read immediately.
      this.game.registry.set('faint:weakenedUntil', this.faintEvent.weakenedUntil);
    }

    // Start the 4-phase sequence.
    this.runPhase1(cx, cy);
  }

  // ---- Phase 1: Desaturation + gentle shake (0-2s) ----

  private runPhase1(cx: number, cy: number): void {
    // Attach a desaturation filter to the active game camera.
    // We look for the first active non-overlay scene camera.
    const gameScenes = this.scene.manager.scenes.filter(
      (s) =>
        s !== this &&
        s.scene.isActive() &&
        s.cameras?.main &&
        s.scene.key !== 'UIScene' &&
        s.scene.key !== 'AmbientOverlayScene',
    );

    for (const gs of gameScenes) {
      const cam = gs.cameras.main;
      if (cam?.filters?.external) {
        try {
          this.cmFilter = cam.filters.external.addColorMatrix();
          this.targetCamera = cam;
          break;
        } catch {
          // Filter not supported; proceed without.
        }
      }
    }

    // Animate desaturation from 0 to 0.7 over Phase 1 duration.
    if (this.cmFilter) {
      const cm = this.cmFilter;
      this.tweens.addCounter({
        from: 0,
        to: 70,
        duration: PHASE_1_DURATION,
        ease: 'Sine.easeIn',
        onUpdate: (tween: Phaser.Tweens.Tween) => {
          const desat = (tween.getValue?.() ?? 0) / 100;
          cm.colorMatrix.reset();
          // Negative saturation = desaturation. 0.7 desaturation matches spec.
          cm.colorMatrix.saturate(-desat);
        },
      });
    }

    // Gentle camera shake on our own overlay camera — subtle screen wobble.
    this.cameras.main.shake(PHASE_1_DURATION, 0.002);

    // After Phase 1, start Phase 2.
    this.time.delayedCall(PHASE_1_DURATION, () => this.runPhase2(cx, cy));
  }

  // ---- Phase 2: Poetic text (2-5s) ----

  private runPhase2(cx: number, cy: number): void {
    const text = this.add
      .text(cx, cy, 'The world grew heavy...', {
        fontFamily: TITLE_FONT,
        fontSize: '24px',
        color: COLOR_INK,
        fontStyle: 'italic',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0)
      .setScrollFactor(0)
      .setDepth(10);

    // Fade in
    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: PHASE_2_FADE_IN,
      ease: 'Sine.easeOut',
    });

    // Hold, then fade out
    this.time.delayedCall(PHASE_2_FADE_IN + PHASE_2_HOLD, () => {
      this.tweens.add({
        targets: text,
        alpha: 0,
        duration: PHASE_2_FADE_OUT,
        ease: 'Sine.easeIn',
        onComplete: () => text.destroy(),
      });
    });

    // After Phase 2, start Phase 3.
    const phase2Total = PHASE_2_FADE_IN + PHASE_2_HOLD + PHASE_2_FADE_OUT;
    this.time.delayedCall(phase2Total, () => this.runPhase3(cx, cy));
  }

  // ---- Phase 3: Fade to deep blue (5-7s) ----

  private runPhase3(cx: number, cy: number): void {
    this.fadeRect = this.add
      .rectangle(cx, cy, this.scale.width, this.scale.height, DEEP_BLUE, 0)
      .setScrollFactor(0)
      .setDepth(20);

    this.tweens.add({
      targets: this.fadeRect,
      alpha: 1,
      duration: PHASE_3_FADE_DURATION,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        // Remove the desaturation filter now that we're fully faded.
        this.removeDesatFilter();
        this.runPhase4(cx, cy);
      },
    });
  }

  // ---- Phase 4: Recovery wake (7-9s) ----

  private runPhase4(cx: number, cy: number): void {
    if (!this.fadeRect) return;

    // Fade back from deep blue.
    this.tweens.add({
      targets: this.fadeRect,
      alpha: 0,
      duration: PHASE_4_FADE_IN,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.fadeRect?.destroy();
        this.fadeRect = null;
      },
    });

    // Recovery text
    const recoveryText = this.add
      .text(cx, cy, '...but the hearth remembered you.', {
        fontFamily: TITLE_FONT,
        fontSize: '24px',
        color: COLOR_INK,
        fontStyle: 'italic',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0)
      .setScrollFactor(0)
      .setDepth(30);

    // Fade in the recovery text
    this.tweens.add({
      targets: recoveryText,
      alpha: 1,
      duration: PHASE_4_TEXT_FADE_IN,
      ease: 'Sine.easeOut',
    });

    // Hold, then fade out and finish
    this.time.delayedCall(PHASE_4_TEXT_FADE_IN + PHASE_4_TEXT_HOLD, () => {
      this.tweens.add({
        targets: recoveryText,
        alpha: 0,
        duration: PHASE_4_TEXT_FADE_OUT,
        ease: 'Sine.easeIn',
        onComplete: () => {
          recoveryText.destroy();
          this.finishSequence();
        },
      });
    });
  }

  // ---- Cleanup ----

  private removeDesatFilter(): void {
    if (this.cmFilter && this.targetCamera) {
      try {
        this.targetCamera.filters.external.remove(this.cmFilter);
      } catch {
        // Filter may already have been removed.
      }
      this.cmFilter = null;
      this.targetCamera = null;
    }
  }

  private finishSequence(): void {
    // Signal the game that the faint cinematic is complete.
    this.game.events.emit('faint:cinematic-complete');
    this.scene.stop(FAINT_OVERLAY_SCENE_KEY);
  }

  shutdown(): void {
    // Ensure filters are cleaned up if scene is stopped externally.
    this.removeDesatFilter();
    this.fadeRect = null;
    this.faintEvent = null;
  }
}
